import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Building, MapPin, Filter } from "lucide-react";
import { ProviderFilters } from '../types/providers.types';
import { SaveFilterButton } from '@/shared/components/ui/SaveFilterButton';
import { SavedFilters } from '@/shared/components/ui/SavedFilters';
import { AppliedFilterEditor } from '@/shared/components/ui/AppliedFilterEditor';
import { Filterset } from '@/app/services/filtersetService';
import { Select } from '@/shared/components/ui/Select';
import { ServiceAreaSelect } from '@/shared/components/ui/ServiceAreaSelect';


interface ProvidersSidebarProps {
    filters: ProviderFilters;
    onFilterChange: (filterName: keyof ProviderFilters, value: unknown) => void;
    onReset: () => void;
    onApply: () => void;
    onSave?: (name: string, isShared: boolean) => Promise<void>;
    savedFilters?: Filterset[] | undefined;
    onApplyFilterset?: (filterset: Filterset) => void;
    onUpdateFilterset?: (id: number, data: Partial<Filterset>) => Promise<void>;
    onDeleteFilterset?: (id: number) => Promise<void>;
    isLoadingSavedFilters?: boolean;
    activeSavedFilterId?: number | null;
    currentFilterExpression?: string;
}

const ProvidersSidebar: React.FC<ProvidersSidebarProps> = ({ 
    filters, 
    onFilterChange, 
    onReset, 
    onApply,
    onSave = async () => {},
    savedFilters = [],
    onApplyFilterset = () => {},
    onUpdateFilterset,
    onDeleteFilterset,
    isLoadingSavedFilters = false,
    activeSavedFilterId,
    currentFilterExpression,
}) => {
    const { t } = useTranslation();
    const [isSavedFiltersOpen, setIsSavedFiltersOpen] = React.useState(false);
    const sidebarRef = useRef<HTMLElement>(null);

    // Focus the first filter field (Provider type button) when the sidebar mounts
    useEffect(() => {
        const timer = setTimeout(() => {
            const btn = sidebarRef.current?.querySelector<HTMLButtonElement>(
                '[data-testid="provider-type-filter"]'
            );
            btn?.focus();
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    const activeFilterset = activeSavedFilterId != null && activeSavedFilterId !== -1
        ? savedFilters?.find((filter) => filter.id === activeSavedFilterId)
        : undefined;

    return (
        <aside ref={sidebarRef} className="w-full bg-surface flex flex-col h-[calc(100vh-3rem)] text-[14px]" data-testid="providers-sidebar">
            {/* Header - Matching FiltersPanel */}
            <div className="flex justify-between items-center px-6 h-[66px] bg-surface-alt border-b border-sidebarHeaderBorder flex-shrink-0" data-testid="sidebar-header">
                <h2 className="text-[1rem] flex items-center text-sidebarHeader gap-2 font-medium">
                    <Filter className="w-5 h-5 text-primary" /> {t('common.filters')}
                </h2>
                <div className="flex items-center gap-2">
                    <SaveFilterButton onSave={onSave} currentFilters={filters} />
                    <button
                        className="h-8 text-sm px-2 border border-border bg-surface rounded text-text hover:bg-surface-alt shadow-sm"
                        onClick={onReset}
                        data-testid="filters-reset-button"
                    >
                        {t('common.reset')}
                    </button>
                    <button
                        className="h-8 text-sm px-2 border border-border bg-primary rounded text-on-primary hover:bg-primary/90 shadow-sm"
                        onClick={onApply}
                        data-testid="filters-apply-button"
                    >
                        {t('common.apply')}
                    </button>
                </div>
            </div>
            {activeFilterset && onUpdateFilterset && (
                <AppliedFilterEditor
                    filterset={activeFilterset}
                    onUpdate={onUpdateFilterset}
                    currentExpression={currentFilterExpression}
                />
            )}
            <div className="space-y-6 flex-1 min-h-0 overflow-y-auto p-4">
                <div>
                    <Select
                        label={
                            <span className="flex items-center">
                              <Building className="mr-2 text-primary h-4 w-4" /> {t('providers.sidebar.providerType')}
                            </span>
                        }
                        labelTitle={t('providers.sidebar.providerType')}
                        value={filters.provider_type}
                        onChange={(e) => onFilterChange('provider_type', e.target.value)}
                        options={[
                            { value: 'No Filters', label: t('providers.sidebar.noFilters') },
                            { value: 'NORMAL', label: t('providers.sidebar.normal') },
                            { value: 'RESTRICTED', label: t('providers.sidebar.restricted') },
                            { value: 'EXTERNAL_PROVIDER', label: t('providers.sidebar.external') },
                            { value: 'PUBLIC_TRANSPORT', label: t('providers.sidebar.publicTransportation') },
                            { value: 'INDEPENDENT_PROVIDER', label: t('providers.sidebar.independent') },
                            { value: 'TRIPS_ONLY', label: t('providers.sidebar.tripsOnly') },
                        ]}
                        data-testid="provider-type-filter"
                        buttonClassName="!bg-background"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-text mb-2 flex items-center" title={t('providers.sidebar.serviceArea')}>
                      <MapPin className="mr-2 text-primary h-4 w-4" /> {t('providers.sidebar.serviceArea')}
                    </label>
                    <ServiceAreaSelect
                        id="service-area-filter"
                        value={filters.service_areas.map(String)}
                        onChange={(selected: string[]) => onFilterChange('service_areas', selected.map(Number))}
                        placeholder={t('providers.sidebar.allAreas')}
                        data-testid="service-area-filter"
                        linkItems
                    />
                </div>
                {/* Warning Status filter hidden — client-side only filter causes pagination mismatch */}
            </div>

            {/* Footer - Saved Filters */}
            <SavedFilters
                isOpen={isSavedFiltersOpen}
                onToggle={() => setIsSavedFiltersOpen(!isSavedFiltersOpen)}
                savedFilters={savedFilters || []}
                isLoading={isLoadingSavedFilters}
                onApplyFilterset={onApplyFilterset}
                {...(onUpdateFilterset ? { onUpdate: onUpdateFilterset } : {})}
                activeFiltersetId={activeSavedFilterId ?? null}
                {...(currentFilterExpression !== undefined ? { currentFilterExpression } : {})}
                {...(onDeleteFilterset ? { onDelete: onDeleteFilterset } : {})}
                showSearch={true}
            />
        </aside>
    );
};

export default ProvidersSidebar;
